import { useEffect, useState } from 'react'
import { Position, Rect, Transform } from 'types'
import { rectFromPosition, transformOriginMultiplier, transformPosition } from 'utils'

type Options = {
    aspectRatio?: number
    bounds: Rect
    transform?: Transform
}

const useDragPosition = (crop: Position, options: Options): Position => {
    const [dragPosition, setDragPosition] = useState<Position>(crop)
    const { aspectRatio, bounds, transform } = options

    useEffect(() => {
        if (!transform) {
            setDragPosition(crop)
            return
        }

        const { origin } = transform
        const position = transformPosition(crop, transform)
        const multiplier = origin ? transformOriginMultiplier(origin) : null
        const prevRect = rectFromPosition(dragPosition)

        const adjustHeightToAspectRatio = () => {
            if (!multiplier) return

            if (aspectRatio) {
                position.height = Math.round(position.width / aspectRatio)
            }

            position.y = multiplier.y > 0 ? prevRect.bottom - position.height : prevRect.top
        }

        const adjustWidthToAspectRatio = () => {
            if (!multiplier) return

            if (aspectRatio) {
                position.width = Math.round(position.height * aspectRatio)
            }

            position.x = multiplier.x > 0 ? prevRect.right - position.width : prevRect.left
        }

        // restrict to left boundary
        if (position.x < bounds.left) {
            position.x = bounds.left

            if (multiplier) {
                position.width = prevRect.right - position.x
                adjustHeightToAspectRatio()
            }
        }

        // restrict to top boundary
        if (position.y < bounds.top) {
            position.y = bounds.top

            if (multiplier) {
                position.height = prevRect.bottom - position.y
                adjustWidthToAspectRatio()
            }
        }

        // restrict to right boundary
        if (position.x + position.width > bounds.right) {
            if (multiplier) {
                position.width = bounds.right - prevRect.left
                adjustHeightToAspectRatio()
            }

            position.x = bounds.right - position.width
        }

        // restrict to bottom boundary
        if (position.y + position.height > bounds.bottom) {
            if (multiplier) {
                position.height = bounds.bottom - prevRect.top
                adjustWidthToAspectRatio()
            }
            position.y = bounds.bottom - position.height
        }

        setDragPosition(position)
    }, [aspectRatio, bounds, crop, transform])

    return dragPosition
}

export default useDragPosition
